<template>
    <div v-if="stocks">
        <div class="col-12 mb-4 d-flex justify-content-between align-items-baseline">
            <h4>Stock</h4>
            <div class="d-flex align-content-center">
                <AppDatePicker :value="stocksDate"
                               placeholder="Stocks date"
                               @input="getByDate($event)"
                               allow-empty="true"
                               :disabled="loading"
                               class="w-auto mb-sm-0 mb-2 mr-2"/>
                <AppSelect :value="stockCurrency"
                           :options="currencies"
                           :allow-empty="true"
                           track-by="code"
                           :disabled="loading"
                           label-field="name"
                           placeholder="Select currency"
                           @input="selectCurrency($event)"
                           class="d-sm-flex mb-0 mr-2"/>
                <a class="btn btn-outline-secondary"
                   :disabled="loading"
                   target="_blank"
                   href=""
                   @click.prevent="exportToXlSX">
                    Export to XLSX
                </a>
            </div>
        </div>
        <VueGoodTable :columns="columns"
                      :rows="rows"
                      :isLoading.sync="loading"
                      ref="stocks"
                      :groupOptions="{
                      enabled: true,
                      headerPosition: 'bottom',
                      }"
                      styleClass="vgt-table condensed"
                      class="vgt-table--card">
            <template slot="table-header-row" slot-scope="props">
                <span v-if="props.column.field === 'warehouse.name'">
                    <span v-if="props.row.label !== 'Grand total'">
                        Region: {{ props.row.label }}
                    </span>
                    <span v-else>
                        {{ props.row.label }}
                    </span>

                </span>
                <span v-if="props.column.field === 'quantity'">
                      {{ quantityPerRegion(props.row.label)| currency(2, ' kg') }}
                </span>
                <span v-if="props.column.field === 'value'">
                      {{ valuePerRegion(props.row.label) | currency(2, selectedCurrencyCode) }}
                </span>
            </template>
        </VueGoodTable>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { VueGoodTable } from 'vue-good-table';
import download from 'downloadjs';
import formatCurrency from '../../filters/currency.filter';
import AppSelect from '../form/AppSelect';
import AppDatePicker from '../form/AppDatePicker';

export default {
  components: {
    AppDatePicker,
    AppSelect,
    VueGoodTable,
  },
  filters: {
    currency: formatCurrency,
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      stocks: 'warehouses/stocks',
      regions: 'regions/regions',
      currencies: 'currencyRates/currencies',
    }),
    ...mapState({
      stockCurrency: (state) => state.profile.stockCurrency,
      stocksDate: (state) => state.warehouses.stocksDate,
    }),
    columns() {
      return [
        {
          label: 'Warehouse',
          field: 'warehouse.name',
          filterOptions: {
            enabled: true,
          },
        },
        {
          label: 'Product',
          field: 'product.name',
          filterOptions: {
            enabled: true,
          },
        },
        {
          label: 'Product type',
          field: 'types',
          filterOptions: {
            enabled: true,
          },
        },
        {
          label: `Price (${this.selectedCurrencyCode}/kg)`,
          field: 'unit',
        },
        {
          label: 'Quantity (kg)',
          field: 'quantity',
        },
        {
          label: `Value (${this.selectedCurrencyCode})`,
          field: 'value',
        },
        {
          label: 'Region',
          field: 'region',
          filterOptions: {
            enabled: true,
          },
        },
      ];
    },
    selectedCurrencyCode() {
      return this.stockCurrency ? this.stockCurrency.code : 'EUR';
    },
    stockEntries() {
      return this.stocks.map((stock) => ({
        warehouse: stock.warehouse,
        product: stock.product,
        types: stock.product ? stock.product.types
          .reduce((accumulator, currentValue) => `${accumulator + currentValue.name} `, '') : null,
        unit: formatCurrency(stock.value / stock.quantity, 2, ` ${this.selectedCurrencyCode}/kg`),
        quantity: formatCurrency(stock.quantity, 2, ' kg'),
        region: this.regions.filter(
          (region) => region.id === (stock.warehouse ? stock.warehouse.location.region_id : null),
        )
          .reduce((accumulator, currentValue) => `${currentValue.name}`, ''),
        value: formatCurrency(stock.value, 2, ` ${this.selectedCurrencyCode}`),
      }));
    },
    rows() {
      const regions = [...this.regions];

      regions.push({ name: 'Grand total' });

      // callback function to get only related stocks to region
      const stocksByRegion = (stock, regionId) => (
        stock.warehouse ? stock.warehouse.location.region_id === regionId : false);

      return regions.map((region) => ({
        label: region.name,
        // group all related stocks to region in children property
        children: this.stockEntries.filter((stock) => stocksByRegion(stock, region.id)),
      }));
    },
  },
  mounted() {
    this.getCurrencies();
    this.getStocks();
  },
  methods: {
    getByDate(date) {
      this.$store.commit('warehouses/stocksDate', date);
      this.getStocks();
    },
    // we fetch related lines from table, format them and calculate exact totals
    quantityPerRegion(regionName) {
      if (!this.$refs.stocks) {
        return null;
      }

      const relatedEntries = this.$refs.stocks.filteredRows
        .filter((region) => (regionName === 'Grand total' ? true : region.label === regionName));

      const quantityPerRegion = relatedEntries.map((stock) => ({
        children: stock.children.reduce((accumulator, currentValue) => accumulator
            + parseFloat(currentValue.quantity.replace(/\s+/g, '')), 0),
      }));

      return quantityPerRegion.reduce((accumulator, currentValue) => accumulator
        + currentValue.children, 0);
    },
    valuePerRegion(regionName) {
      if (!this.$refs.stocks) {
        return null;
      }

      const relatedEntries = this.$refs.stocks.filteredRows
        .filter((region) => (regionName === 'Grand total' ? true : region.label === regionName));

      const valuePerRegion = relatedEntries.map((stock) => ({
        children: stock.children.reduce((accumulator, currentValue) => accumulator
            + parseFloat(currentValue.value.replace(/\s+/g, '')), 0),
      }));

      return valuePerRegion.reduce((accumulator, currentValue) => accumulator
        + currentValue.children, 0);
    },
    selectCurrency(currency) {
      this.$store.commit('profile/stockCurrency', currency);
      this.getStocks();
    },
    exportToXlSX() {
      this.$store.dispatch('warehouses/exportToXlSX', { stocksData: this.stockEntries })
        .then((res) => {
          download(res.file, res.name, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        });
    },
    getCurrencies() {
      this.$store.dispatch('currencyRates/getCurrencies');
    },
    getStocks() {
      this.loading = true;

      this.$store.dispatch('warehouses/getStocks', { currency: this.selectedCurrencyCode })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
